"use client";

import { useEffect, useState } from "react";

/** What the modifier key is called on this platform. */
export interface PlatformModifier {
  /** Rendered in a `kbd`, e.g. "⌘" on a Mac and "Ctrl" elsewhere. */
  symbol: string;
  /** For `aria-keyshortcuts` and titles, which want words rather than glyphs. */
  name: "Meta" | "Control";
}

const MAC = { symbol: "⌘", name: "Meta" } as const;
const OTHER = { symbol: "Ctrl", name: "Control" } as const;

/**
 * The modifier key label for the current platform.
 *
 * The hotkey itself accepts both Cmd and Ctrl, so this only affects what we
 * *tell* the user. Hardcoding the Mac glyph made the hint wrong on Windows,
 * which is worse than showing nothing: a shortcut you are told incorrectly is a
 * shortcut you try once and abandon.
 *
 * Returns null until mounted. Platform is only knowable in the browser, and
 * rendering one label on the server and another on the client is a hydration
 * mismatch — so callers render nothing for the first frame rather than flicker
 * from the wrong answer to the right one.
 *
 * @returns The platform's modifier, or null before mount.
 */
export function usePlatformModifier(): PlatformModifier | null {
  // States
  const [modifier, setModifier] = useState<PlatformModifier | null>(null);

  // Effects
  useEffect(() => {
    // `navigator.platform` is deprecated but still the most reliable signal, and
    // userAgent covers the browsers that have dropped it.
    const source = `${navigator.platform ?? ""} ${navigator.userAgent ?? ""}`;
    setModifier(/mac|iphone|ipad|ipod/i.test(source) ? MAC : OTHER);
  }, []);

  return modifier;
}
